import { css, styled } from 'canoe';
import moment from 'moment';
import React from 'react';

const Container = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.OnyxGrayD10};
    color: ${theme.colors.White};
    padding: ${theme.spaces.xs};
    border-radius: ${theme.borderRadius.small};
  `}
`;

const Row = styled.div<{ color: string }>`
  ${({ theme, color }) => css`
    display: flex;
    flex-direction: column;
    border-left: 3px solid ${color};
    padding-left: ${theme.spaces.xs};
    margin-bottom: ${theme.spaces.xs};
    line-height: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  `}
`;

const TooltipTitle = styled.div`
  margin-bottom: 8px;
  line-height: 16px;
`;

type Payload = {
  color: string;
  dataKey: string;
  name: string;
  payload: {
    date?: string;
    name?: string;
  };
  type?: string;
  unit?: string;
  stroke: string;
  value: string | number;
};

interface Props {
  payload: Payload[];
}

const dateTooltip = (row: any, index: number, dateFormat = 'D MMM YY') => {
  const { date } = row.payload || {};
  if (date) {
    return moment(date).format(dateFormat);
  }
  return '';
};

export const HistogramTooltip = ({ payload }: Props) => {
  const title = payload?.[0]?.payload?.name;

  return (
    <Container>
      {title && <TooltipTitle>{title}</TooltipTitle>}
      {payload?.map((row: any) => (
        <Row key={row.dataKey} color={row.color}>
          <strong>
            {row.payload[row.dataKey].toLocaleString('en-GB')}&nbsp;{row.name}
          </strong>
        </Row>
      ))}
    </Container>
  );
};

export const CustomTooltip = (
  legend: string,
  isMultiSeries?: boolean,
  dateFormat?: string,
  unit?: string,
) => ({ payload }: Props) => {
  const withUnit = (r: any) => (unit ? `${r}%` : r);
  return (
    <Container>
      {payload && payload[0] && (
        <TooltipTitle>{dateTooltip(payload[0], 0, dateFormat)}</TooltipTitle>
      )}
      {payload?.map((row: any) => (
        <Row key={row.dataKey} color={row.color}>
          {isMultiSeries && <strong>{row.name}</strong>}
          <span>
            <strong>{withUnit(row.payload[row.dataKey].toLocaleString('en-GB'))}&nbsp;</strong>
            {legend}
          </span>
        </Row>
      ))}
    </Container>
  );
};

export const CustomTooltipWithMultipleLegends = (
  legends: Record<string, string>,
  isSegmentation: boolean,
  dateFormat?: string,
  sort?: string[],
) => ({ payload }: Props) => {
  const sortedPayload = sort
    ? payload.sort((a, b) => sort.indexOf(a.dataKey) - sort.indexOf(b.dataKey))
    : payload;
  return (
    <Container>
      {sortedPayload?.map((row: any, index: number) => (
        <Row key={row.dataKey} color={row.color}>
          <strong>{isSegmentation ? row.name : dateTooltip(row, index, dateFormat)}</strong>
          <span>
            <strong>{row.payload[row.dataKey].toLocaleString('en-GB')} </strong>
            {legends[row.dataKey]}
          </span>
        </Row>
      ))}
    </Container>
  );
};
